import { Dispatch, RefObject, SetStateAction } from "react";

import { Box } from "@mui/material";

import { usePersistedDragResize } from "../hooks/usePersistedDragResize";

type BoxResizeHandleProps = {
	position?: "left" | "right" | "top" | "bottom";
	// External state management
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	panelRef: RefObject<HTMLDivElement>;
	// Resize configuration
	storageKey: string;
	defaultWidth: number;
	minWidth: number;
	maxWidth: number;
	direction?: "left" | "right";
	hoverColor?: string;
	draggingColor?: string;
};

/**
 * A resize handle component for draggable panel edges.
 * Encapsulates drag-to-resize logic with localStorage persistence.
 * Displays a thin interactive strip that changes color on hover and while dragging.
 */
export function BoxResizeHandle({
	position = "left",
	width,
	setWidth,
	panelRef,
	storageKey,
	defaultWidth,
	minWidth,
	maxWidth,
	direction = "right",
	hoverColor = "primary.light",
	draggingColor = "primary.main",
}: BoxResizeHandleProps) {
	// Use the drag resize hook with external state
	const { isDragging, handleMouseDown } = usePersistedDragResize({
		storageKey,
		defaultWidth,
		minWidth,
		maxWidth,
		direction,
		width,
		setWidth,
		panelRef,
	});

	const positionStyles = {
		left: { left: 0, top: 0, bottom: 0, width: "4px", cursor: "col-resize" },
		right: { right: 0, top: 0, bottom: 0, width: "4px", cursor: "col-resize" },
		top: { top: 0, left: 0, right: 0, height: "4px", cursor: "row-resize" },
		bottom: { bottom: 0, left: 0, right: 0, height: "4px", cursor: "row-resize" },
	};

	return (
		<Box
			onMouseDown={handleMouseDown}
			sx={{
				position: "absolute",
				...positionStyles[position],
				backgroundColor: isDragging ? draggingColor : "transparent",
				"&:hover": {
					backgroundColor: hoverColor,
				},
				transition: "background-color 0.2s",
				zIndex: 1,
			}}
		/>
	);
}
